/**
 * TJC Plus — cart membership (activate, switch plan, remove).
 * Updates cart via AJAX + section morph (no full reload unless morph fails).
 */
import {
  appendSectionsToBody,
  refreshCartPageAfterMutationWithFallback,
} from '@theme/cart-section-refresh';
import { fetchCartJson, seedCartCache } from '@theme/cart-api';

(function () {
  'use strict';

  var isSwitchingPlan = false;
  var isMembershipBusy = false;

  function getCartUrl() {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.cart_url) {
      return window.Shopify.routes.cart_url;
    }
    return '/cart';
  }

  function showLoader() {
    if (typeof window.showTjcLoader === 'function') {
      window.showTjcLoader();
    } else {
      var loader = document.getElementById('tjc-global-loader');
      if (loader) loader.classList.add('active');
    }
  }

  function setLoaderProgress(percent) {
    if (typeof window.setTjcLoaderProgress === 'function') {
      window.setTjcLoaderProgress(percent);
    }
  }

  function hideLoader() {
    if (typeof window.hideTjcLoader === 'function') {
      window.hideTjcLoader();
    } else {
      var loader = document.getElementById('tjc-global-loader');
      if (loader) loader.classList.remove('active');
    }
  }

  function releaseMembershipBusy() {
    isMembershipBusy = false;
    isSwitchingPlan = false;
    hideLoader();
  }

  function lineItemIsTjcPlusMembership(item) {
    if (typeof window.tjcPlusIsMembershipLineItem === 'function') {
      return window.tjcPlusIsMembershipLineItem(item);
    }
    return false;
  }

  function getLineSellingPlanId(item) {
    if (!item || !item.selling_plan_allocation || !item.selling_plan_allocation.selling_plan) return null;
    return item.selling_plan_allocation.selling_plan.id;
  }

  function parseNumericId(value) {
    if (value == null || value === '') return NaN;
    var s = String(value).trim();
    var gidMatch = s.match(/SellingPlan\/(\d+)/i) || s.match(/ProductVariant\/(\d+)/i);
    if (gidMatch) return parseInt(gidMatch[1], 10);
    return parseInt(s, 10);
  }

  function parsePlanConfig(plan) {
    var isMonthly = plan === 'monthly';
    return {
      isMonthly: isMonthly,
      variantId: parseNumericId(
        isMonthly ? window.TJC_PLUS_MONTHLY_VARIANT_ID : window.TJC_PLUS_YEARLY_VARIANT_ID,
      ),
      sellingPlanId: parseNumericId(
        isMonthly ? window.TJC_PLUS_MONTHLY_PLAN_ID : window.TJC_PLUS_YEARLY_PLAN_ID,
      ),
    };
  }

  function cartHasSameMembershipPlan(cart, variantId, sellingPlanId) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!lineItemIsTjcPlusMembership(item)) continue;
      if (parseInt(item.variant_id, 10) !== parseInt(variantId, 10)) continue;
      var existingPlan = getLineSellingPlanId(item);
      if (existingPlan != null && parseInt(existingPlan, 10) === parseInt(sellingPlanId, 10)) return true;
    }
    return false;
  }

  function findMembershipLine(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (lineItemIsTjcPlusMembership(items[i])) {
        return { item: items[i], lineNumber: i + 1 };
      }
    }
    return null;
  }

  function isCartPagePath() {
    var p = window.location.pathname || '';
    return p === '/cart' || /\/cart$/.test(p);
  }

  function goToCartOrMorph(cartData, action) {
    if (isCartPagePath() && cartData) {
      return refreshCartPageAfterMutationWithFallback(cartData, {
        action: action || 'update',
        source: 'tjc-membership',
      });
    }
    window.location.href = getCartUrl();
    return Promise.resolve();
  }

  function showTjcPlusError(message) {
    var text = message || 'Something went wrong. Please try again.';
    if (typeof window.tjcPlusShowError === 'function') {
      window.tjcPlusShowError(text);
    } else {
      console.error('TJC Plus:', text);
    }
  }

  function revertMembershipPlanRadios(selectedPlan) {
    var activePlan = selectedPlan === 'monthly' ? 'monthly' : 'yearly';
    var revertPlan = activePlan === 'monthly' ? 'yearly' : 'monthly';

    document.querySelectorAll('input[name^="membership_plan_select_"]').forEach(function (input) {
      input.checked = input.value === revertPlan;
    });

    document.querySelectorAll('.tjc-membership-card .tjc-plan').forEach(function (label) {
      var radio = label.querySelector('input[type="radio"]');
      if (radio) {
        label.classList.toggle('active', radio.checked);
      }
    });
  }

  function fetchCart() {
    return fetchCartJson();
  }

  function parseCartError(res, data, fallback) {
    var msg = (data && (data.description || data.message)) || res.statusText || fallback;
    return new Error(typeof msg === 'string' ? msg : fallback);
  }

  function postCartJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(appendSectionsToBody(body)),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw parseCartError(res, data, 'Cart request failed');
        seedCartCache(data);
        return data;
      });
    });
  }

  function clearTjcPlusFromCart(existingCart) {
    return Promise.resolve(existingCart || fetchCart()).then(function (cart) {
      var updates = {};
      (cart.items || []).forEach(function (item) {
        if (!lineItemIsTjcPlusMembership(item)) return;
        if (!item.key) {
          console.warn('TJC Plus: membership line missing key', item);
          return;
        }
        updates[item.key] = 0;
      });

      if (Object.keys(updates).length === 0) {
        return Promise.resolve(cart);
      }

      return postCartJson('/cart/update.js', { updates: updates });
    });
  }

  function addMembershipToCart(variantId, sellingPlanId) {
    return postCartJson('/cart/add.js', {
      items: [
        {
          id: variantId,
          quantity: 1,
          selling_plan: sellingPlanId,
        },
      ],
    });
  }

  function changeMembershipLine(lineNumber, variantId, sellingPlanId) {
    return postCartJson('/cart/change.js', {
      line: lineNumber,
      quantity: 1,
      id: variantId,
      selling_plan: sellingPlanId,
    });
  }

  function membershipLineMatchesTarget(item, variantId, sellingPlanId) {
    if (!item || parseInt(item.variant_id, 10) !== parseInt(variantId, 10)) return false;
    var existingPlan = getLineSellingPlanId(item);
    return existingPlan != null && parseInt(existingPlan, 10) === parseInt(sellingPlanId, 10);
  }

  function removeAndAddMembership(variantId, sellingPlanId, existingCart) {
    return clearTjcPlusFromCart(existingCart).then(function (cartAfterRemove) {
      return addMembershipToCart(variantId, sellingPlanId);
    });
  }

  function replaceMembershipPlan(variantId, sellingPlanId, initialCart) {
    return Promise.resolve(initialCart || fetchCart()).then(function (cart) {
      var membership = findMembershipLine(cart);

      if (!membership || !membership.item) {
        return addMembershipToCart(variantId, sellingPlanId);
      }

      if (membershipLineMatchesTarget(membership.item, variantId, sellingPlanId)) {
        return cart;
      }

      var sameVariant =
        parseInt(membership.item.variant_id, 10) === parseInt(variantId, 10);

      // Monthly/yearly are different variants — change.js cannot swap variant + selling plan.
      if (!sameVariant) {
        return removeAndAddMembership(variantId, sellingPlanId, cart);
      }

      return changeMembershipLine(membership.lineNumber, variantId, sellingPlanId).catch(function () {
        return removeAndAddMembership(variantId, sellingPlanId, cart);
      });
    });
  }

  function runMembershipAction(options) {
    var plan = options.plan;
    var onSuccess = options.onSuccess;
    var onError = options.onError;
    var skipSamePlanReload = options.skipSamePlanReload;
    var config = parsePlanConfig(plan);

    if (!config.variantId || isNaN(config.variantId) || !config.sellingPlanId || isNaN(config.sellingPlanId)) {
      console.error('TJC Plus: missing window.TJC_PLUS_* IDs');
      showTjcPlusError('Membership plan not configured. Please contact support.');
      if (onError) onError({ configError: true });
      return;
    }

    if (isMembershipBusy) return;

    isMembershipBusy = true;
    showLoader();
    setLoaderProgress(10);

    fetchCart()
      .then(function (cart) {
        setLoaderProgress(25);
        if (cartHasSameMembershipPlan(cart, config.variantId, config.sellingPlanId)) {
          if (skipSamePlanReload) {
            return Promise.reject({ code: 'SAME_PLAN' });
          }
          return goToCartOrMorph(cart, 'update').then(function () {
            return { done: true };
          });
        }
        return replaceMembershipPlan(config.variantId, config.sellingPlanId, cart);
      })
      .then(function (result) {
        setLoaderProgress(55);
        if (!result) return;
        if (result.done) {
          if (onSuccess) onSuccess();
          return;
        }
        if (onSuccess) onSuccess();
        setLoaderProgress(70);
        return goToCartOrMorph(result, 'add');
      })
      .then(function () {
        releaseMembershipBusy();
      })
      .catch(function (err) {
        releaseMembershipBusy();

        if (err && err.code === 'SAME_PLAN') return;

        if (onError) onError(err);
        console.error('TJC Plus membership action error:', err);
        showTjcPlusError(err && err.message ? err.message : undefined);
      });
  }

  function switchMembershipPlan(plan) {
    if (isSwitchingPlan || isMembershipBusy) return;

    isSwitchingPlan = true;

    runMembershipAction({
      plan: plan,
      skipSamePlanReload: true,
      onError: function () {
        revertMembershipPlanRadios(plan);
      },
    });
  }

  function removeMembershipOnly(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (isMembershipBusy) return;

    isMembershipBusy = true;
    showLoader();
    setLoaderProgress(15);

    fetchCartJson()
      .then(function (cart) {
        setLoaderProgress(25);
        return clearTjcPlusFromCart(cart);
      })
      .then(function (cartData) {
        setLoaderProgress(50);
        return refreshCartPageAfterMutationWithFallback(cartData, {
          action: 'remove',
          source: 'tjc-membership',
        });
      })
      .then(function () {
        releaseMembershipBusy();
      })
      .catch(function (err) {
        releaseMembershipBusy();
        console.error('TJC Plus remove error:', err);
        showTjcPlusError(err && err.message ? err.message : undefined);
      });
  }

  function getTjcPlusLoginRedirectUrl(plan) {
    var cartPath =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_url) ||
      window.TJC_CART_URL ||
      '/cart';
    var returnTo = cartPath;
    if (plan === 'monthly' || plan === 'yearly') {
      var separator = cartPath.indexOf('?') >= 0 ? '&' : '?';
      returnTo = cartPath + separator + 'tjc_plus_activate=' + encodeURIComponent(plan);
    }
    var loginPath =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.storefront_login_url) ||
      '/customer_authentication/login';
    var baseUrl = window.location.origin;
    var loginFullUrl = loginPath.indexOf('http') === 0 ? loginPath : new URL(loginPath, baseUrl).href;
    var url = new URL(loginFullUrl);
    url.searchParams.set('return_to', returnTo);
    url.searchParams.delete('return_url');
    return url.toString();
  }

  var PENDING_ACTIVATE_STORAGE_KEY = 'tjc_plus_pending_activate';

  function storePendingActivatePlan(plan) {
    if (plan !== 'monthly' && plan !== 'yearly') return;
    try {
      sessionStorage.setItem(PENDING_ACTIVATE_STORAGE_KEY, plan);
    } catch (_) {
      // no-op
    }
  }

  function readPendingActivatePlanFromStorage() {
    try {
      var stored = sessionStorage.getItem(PENDING_ACTIVATE_STORAGE_KEY);
      if (stored === 'monthly' || stored === 'yearly') return stored;
    } catch (_) {
      // no-op
    }
    return null;
  }

  function clearPendingActivatePlanFromUrl() {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      if (!url.searchParams.has('tjc_plus_activate')) return;
      url.searchParams.delete('tjc_plus_activate');
      var next = url.pathname + url.search + url.hash;
      window.history.replaceState({}, '', next);
    } catch (_) {
      // no-op
    }
  }

  function clearPendingActivatePlan() {
    try {
      sessionStorage.removeItem(PENDING_ACTIVATE_STORAGE_KEY);
    } catch (_) {
      // no-op
    }
    clearPendingActivatePlanFromUrl();
  }

  function getPendingActivatePlan() {
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('tjc_plus_activate');
      if (fromUrl === 'monthly' || fromUrl === 'yearly') return fromUrl;
    } catch (_) {
      // no-op
    }
    return readPendingActivatePlanFromStorage();
  }

  function consumePendingActivatePlan() {
    var plan = getPendingActivatePlan();
    if (!plan) return null;
    clearPendingActivatePlan();
    return plan;
  }

  function redirectGuestToLoginForActivate(plan) {
    storePendingActivatePlan(plan);
    window.location.href = getTjcPlusLoginRedirectUrl(plan);
  }

  function processPendingActivateAfterLogin() {
    if (typeof window.TJC_PLUS_CUSTOMER_LOGGED_IN !== 'undefined' && !window.TJC_PLUS_CUSTOMER_LOGGED_IN) {
      return;
    }
    if (window.TJC_PLUS_CUSTOMER_IS_ACTIVE_MEMBER === true) {
      clearPendingActivatePlan();
      return;
    }
    if (isMembershipBusy) return;

    var plan = consumePendingActivatePlan();
    if (!plan) return;

    runMembershipAction({
      plan: plan,
      skipSamePlanReload: false,
    });
  }

  function onActivateClickCapture(event) {
    var btn = event.target.closest('.mcp_activate_btn, .psn-activate-btn-mobile');
    if (!btn || btn.disabled) return;

    var mobile = btn.classList.contains('psn-activate-btn-mobile');
    var inputName = mobile ? 'psn_plan_mobile' : 'psn_plan';
    var selected = document.querySelector('input[name="' + inputName + '"]:checked');
    var plan = selected && selected.value === 'yearly' ? 'yearly' : 'monthly';
    var config = parsePlanConfig(plan);

    if (!config.variantId || isNaN(config.variantId)) {
      config.variantId = parseNumericId(
        btn.getAttribute(plan === 'yearly' ? 'data-variant-yearly' : 'data-variant-monthly'),
      );
    }
    if (!config.sellingPlanId || isNaN(config.sellingPlanId)) {
      config.sellingPlanId = parseNumericId(
        btn.getAttribute(plan === 'yearly' ? 'data-selling-plan-yearly' : 'data-selling-plan-monthly'),
      );
    }

    if (!config.variantId || isNaN(config.variantId)) {
      console.error('TJC Plus: Invalid variant ID');
      showTjcPlusError('Something went wrong. Please refresh.');
      return;
    }

    if (!config.sellingPlanId || isNaN(config.sellingPlanId)) {
      console.error('TJC Plus: Missing selling plan ID');
      showTjcPlusError('Membership plan not configured. Please contact support.');
      return;
    }

    if (typeof window.TJC_PLUS_CUSTOMER_LOGGED_IN !== 'undefined' && !window.TJC_PLUS_CUSTOMER_LOGGED_IN) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      redirectGuestToLoginForActivate(plan);
      return;
    }

    if (window.TJC_PLUS_CUSTOMER_IS_ACTIVE_MEMBER === true) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showTjcPlusError('You are already a TJC Plus member.');
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (isMembershipBusy) return;

    isMembershipBusy = true;
    showLoader();
    setLoaderProgress(10);
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    fetchCart()
      .then(function (cart) {
        setLoaderProgress(25);
        if (cartHasSameMembershipPlan(cart, config.variantId, config.sellingPlanId)) {
          return goToCartOrMorph(cart, 'add').then(function () {
            return Promise.reject({ code: 'SAME_PLAN' });
          });
        }
        return replaceMembershipPlan(config.variantId, config.sellingPlanId, cart);
      })
      .then(function (cartData) {
        if (!cartData) return;
        setLoaderProgress(55);
        return goToCartOrMorph(cartData, 'add');
      })
      .then(function () {
        releaseMembershipBusy();
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      })
      .catch(function (err) {
        releaseMembershipBusy();

        if (err && err.code === 'SAME_PLAN') return;

        console.error('TJC Plus activate error:', err);
        showTjcPlusError(err && err.message ? err.message : undefined);
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      });
  }

  window.switchMembershipPlan = switchMembershipPlan;
  window.removeMembershipOnly = removeMembershipOnly;

  document.addEventListener('click', onActivateClickCapture, true);

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (
      !target ||
      !target.matches ||
      !target.matches('.tjc-membership-card input[type="radio"][name^="membership_plan_select_"]')
    ) {
      return;
    }
    if (target.value === 'monthly' || target.value === 'yearly') {
      switchMembershipPlan(target.value);
    }
  });

  document.addEventListener('click', function (event) {
    var removeBtn = event.target.closest('[data-membership-remove]');
    if (!removeBtn || !removeBtn.closest('.tjc-membership-card')) return;
    removeMembershipOnly(event);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processPendingActivateAfterLogin);
  } else {
    processPendingActivateAfterLogin();
  }
})();